'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import * as RPopover from '@radix-ui/react-popover';
import { SField, type SFieldSize, type SFieldAddonAlign, type SFieldStatus } from '../SField';
import { SDivider } from '../SDivider';
import { SGhostButton } from '../SGhostButton';
import { SIcon, type SIconName } from '../SIcon';
import { type SColor } from '../../lib/color';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { todayStr, monthCells, calcYearMonth, formatDate, WEEK_LABELS } from '../../lib/date';
import { cn } from '../../lib/cn';
import { FLOATING_SLIDE_ANIM } from '../../lib/floating-anim';
import { usePortalContainer } from '../../lib/portal-container';
import { type STooltipProps } from '../STooltip';

export type SDateRangeValue = [string, string] | null;
export type SDateRangePickerSize = SFieldSize;

export interface SDateRangePickerProps {
  value?: SDateRangeValue;
  /** 선택 변경 (sdUpdate) — [start, end] */
  onValueChange?: (range: SDateRangeValue) => void;
  /** 보이는 연·월 변경 (sdViewChange) */
  onViewChange?: (view: { year: number; month: number }) => void;
  size?: SDateRangePickerSize;
  placeholder?: string;
  selectable?: [string, string];
  /** 최대 선택 일수 */
  maxRange?: number;
  /**
   * 시간(시:분) 선택 푸터 사용 여부.
   * true이면 value는 "YYYY-MM-DD HH:mm" 형식이 되고, 캘린더 하단에 시작·종료 시간 입력이 표시된다.
   */
  useTimePicker?: boolean;
  disabled?: boolean;
  width?: number | string;
  name?: string;
  rules?: Rule[];
  status?: SFieldStatus;
  label?: string;
  labelWidth?: number | string;
  icon?: SIconName;
  iconColor?: SColor;
  labelTooltip?: string;
  labelTooltipProps?: Partial<STooltipProps>;
  addonLabel?: string;
  addonAlign?: SFieldAddonAlign;
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  style?: CSSProperties;
}

/** "YYYY-MM-DD HH:mm" 또는 "YYYY-MM-DD"를 날짜/시/분으로 분리 */
function splitDateTime(v?: string | null): { date: string | null; hour: string; minute: string } {
  if (!v) return { date: null, hour: '00', minute: '00' };
  const [date = '', time = ''] = v.split(' ');
  const [hour = '00', minute = '00'] = time.split(':');
  return { date: date || null, hour, minute };
}

function RangeCalendar({
  value,
  selectable,
  maxRange,
  useTimePicker = false,
  onSelect,
  onPendingStartChange,
  onViewChange,
}: {
  value: SDateRangeValue;
  selectable?: [string, string];
  maxRange?: number;
  useTimePicker?: boolean;
  onSelect: (r: [string, string]) => void;
  /** 시작일만 선택된(종료일 대기) 상태를 상위로 전달 — 트리거에 `start ~` 프리뷰 표시용 */
  onPendingStartChange?: (start: string | null) => void;
  onViewChange?: (view: { year: number; month: number }) => void;
}) {
  const today = todayStr();
  const initial = splitDateTime(value?.[0]);
  const base = initial.date || today;
  const [y, setY] = useState(Number(base.split('-')[0]));
  const [m, setM] = useState(Number(base.split('-')[1]));
  // start/end는 날짜(YYYY-MM-DD)만 보관 — 그리드 로직은 시간과 무관하게 동작한다
  const [start, setStart] = useState<string | null>(initial.date);
  const [end, setEnd] = useState<string | null>(splitDateTime(value?.[1]).date);
  const [hover, setHover] = useState<string | null>(null);
  // 시:분은 2자리 미만 입력도 허용하기 위해 문자열로 관리하고 emit/blur 시 패딩한다
  const [sH, setSH] = useState(initial.hour);
  const [sM, setSM] = useState(initial.minute);
  const [eH, setEH] = useState(splitDateTime(value?.[1]).hour);
  const [eM, setEM] = useState(splitDateTime(value?.[1]).minute);
  /** 직접 emit한 값이 value로 되돌아오는 에코를 구분해 시간 입력 clobber를 막는다 */
  const lastEmitted = useRef<string>('');

  useEffect(() => {
    // 자신이 emit한 값이 그대로 돌아온 경우(에코)에는 시간 입력 상태를 건드리지 않는다
    if (useTimePicker && JSON.stringify(value) === lastEmitted.current) return;
    const s = splitDateTime(value?.[0]);
    const e = splitDateTime(value?.[1]);
    setStart(s.date);
    setEnd(e.date);
    setSH(s.hour);
    setSM(s.minute);
    setEH(e.hour);
    setEM(e.minute);
  }, [value, useTimePicker]);

  // 네비게이션: 상단 연도(±1년) + 패널별 월(좌=이전/우=다음) + 오늘 (원본 sd-date-range-picker-calendar)
  const setBase = (ny: number, nm: number) => {
    setY(ny);
    setM(nm);
    onViewChange?.({ year: ny, month: nm });
  };
  const goPrevYear = () => setBase(y - 1, m);
  const goNextYear = () => setBase(y + 1, m);
  const goPrevMonth = () => {
    const n = calcYearMonth(y, m, 'prev');
    setBase(n.year, n.month);
  };
  const goNextMonth = () => {
    const n = calcYearMonth(y, m, 'next');
    setBase(n.year, n.month);
  };
  const goToday = () => {
    const [ty, tm] = today.split('-');
    setBase(Number(ty), Number(tm));
  };
  const nextMonthYM = calcYearMonth(y, m, 'next');

  const addDays = (iso: string, n: number) => {
    const [Y, M, D] = iso.split('-').map(Number);
    const dt = new Date(Y, M - 1, D + n);
    return formatDate(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
  };

  const isDisabled = (d: string) => {
    if (selectable) {
      const [s, e] = selectable;
      if (s !== '' && d < s) return true;
      if (e !== '' && d > e) return true;
    }
    // 원본: pendingStart 기준 ±maxRange 대칭 범위 밖은 비활성
    if (maxRange && maxRange > 0 && start && !end) {
      if (d < addDays(start, -maxRange) || d > addDays(start, maxRange)) return true;
    }
    return false;
  };

  // 선택 상태 계산 (원본 getRangeState). start만 있으면 pending(프리뷰), start&&end면 확정.
  const complete = !!(start && end);
  const rStart = start;
  const previewEnd = !complete && start && hover && hover >= start ? hover : null;
  const rEnd = complete ? end : previewEnd;
  const hasRange = !!(rStart && rEnd && rStart !== rEnd);
  const inRange = (d: string) => !!(rStart && rEnd && d > rStart && d < rEnd);
  const RANGE_BG = 'var(--cmp-datepicker-calendar-range-bg)';

  const pad = (v: string) => String(Number(v) || 0).padStart(2, '0');

  /** 날짜 범위(+ useTimePicker면 시:분)를 결합해 상위로 전달 */
  const emit = (
    dateStart: string,
    dateEnd: string,
    time?: { sH: string; sM: string; eH: string; eM: string },
  ) => {
    const t = time ?? { sH, sM, eH, eM };
    const range: [string, string] = useTimePicker
      ? [`${dateStart} ${pad(t.sH)}:${pad(t.sM)}`, `${dateEnd} ${pad(t.eH)}:${pad(t.eM)}`]
      : [dateStart, dateEnd];
    lastEmitted.current = JSON.stringify(range);
    onSelect(range);
  };

  // 원본: pendingStart 없거나 확정됐거나 "이전 날짜" 클릭 시 → 시작점 리셋(정방향만). 이후 클릭이 >= start면 확정.
  const click = (d: string) => {
    if (isDisabled(d)) return;
    if (!start || complete || d < start) {
      setStart(d);
      setEnd(null);
      setHover(null);
      // 새 범위 선택을 시작하면 시작=00:00, 종료=23:59(하루 전체)로 초기화
      if (useTimePicker) {
        setSH('00');
        setSM('00');
        setEH('23');
        setEM('59');
      }
      onPendingStartChange?.(d);
      return;
    }
    setEnd(d);
    onPendingStartChange?.(null);
    // 종료 기본값은 23:59(하루 끝)
    emit(start, d, useTimePicker ? { sH, sM, eH: '23', eM: '59' } : undefined);
  };

  /** 시:분 입력 — 숫자만/2자리로 제한하고 범위(시 0~23, 분 0~59)로 클램프한 뒤, 범위 확정 상태면 재emit */
  const changeTime = (which: 'start' | 'end', part: 'h' | 'm', raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    const max = part === 'h' ? 23 : 59;
    const next = digits !== '' && Number(digits) > max ? String(max) : digits;
    const nextTime = { sH, sM, eH, eM };
    if (which === 'start') nextTime[part === 'h' ? 'sH' : 'sM'] = next;
    else nextTime[part === 'h' ? 'eH' : 'eM'] = next;
    setSH(nextTime.sH);
    setSM(nextTime.sM);
    setEH(nextTime.eH);
    setEM(nextTime.eM);
    if (start && end) emit(start, end, nextTime);
  };

  const blurTime = (which: 'start' | 'end', part: 'h' | 'm') => {
    if (which === 'start') {
      if (part === 'h') setSH(pad(sH));
      else setSM(pad(sM));
    } else {
      if (part === 'h') setEH(pad(eH));
      else setEM(pad(eM));
    }
  };

  const GRID_COLS = 'grid grid-cols-[repeat(7,var(--cmp-datepicker-calendar-day-width))]';
  const renderPanel = (gy: number, gm: number, isLeft: boolean) => (
    <div className="flex flex-col gap-[var(--cmp-datepicker-calendar-gap)]">
      {/* 월 네비 헤더 (좌패널=이전달만, 우패널=다음달만; 안쪽 화살표는 숨김 placeholder) */}
      <div className="flex w-full items-center gap-[var(--cmp-datepicker-calendar-gap)]">
        <SGhostButton
          icon="chevronLeft"
          size="xxs"
          ariaLabel="prevMonth"
          onClick={goPrevMonth}
          className={cn(!isLeft && 'invisible pointer-events-none')}
        />
        <span className="inline-flex min-w-[40px] flex-1 items-center justify-center text-center text-[14px] font-medium leading-[24px]">
          {gy}.{String(gm).padStart(2, '0')}
        </span>
        <SGhostButton
          icon="chevronRight"
          size="xxs"
          ariaLabel="nextMonth"
          onClick={goNextMonth}
          className={cn(isLeft && 'invisible pointer-events-none')}
        />
      </div>
      <div className={GRID_COLS}>
        {WEEK_LABELS.map(l => (
          <span
            key={l}
            className="flex h-[20px] items-center justify-center text-[12px] font-normal text-[color:var(--cmp-datepicker-calendar-week-color)]"
          >
            {l}
          </span>
        ))}
      </div>
      <div className={cn(GRID_COLS, 'gap-y-[var(--cmp-datepicker-calendar-grid-rowGap)]')}>
        {monthCells(gy, gm).map(cell => {
          // 선택 원(edge): 시작은 항상, 끝은 확정 상태에서만 (프리뷰 끝 셀은 배경만, 원 없음)
          const isEdge =
            cell.inCurrentMonth && (cell.date === rStart || (complete && cell.date === rEnd));
          const between = cell.inCurrentMonth && inRange(cell.date);
          const disabled = cell.inCurrentMonth && isDisabled(cell.date);
          const isToday = cell.inCurrentMonth && today === cell.date;
          // 범위 채우기(원본): 중간=전체, 시작=오른쪽 절반, 끝=전체(range-end full)
          let cellBg: string | undefined;
          if (between) cellBg = RANGE_BG;
          else if (cell.inCurrentMonth && hasRange && cell.date === rStart)
            cellBg = `linear-gradient(to right, transparent 50%, ${RANGE_BG} 50%)`;
          else if (cell.inCurrentMonth && hasRange && cell.date === rEnd) cellBg = RANGE_BG;
          return (
            <div
              key={cell.date}
              className="flex h-[var(--cmp-datepicker-calendar-range-height)] w-[var(--cmp-datepicker-calendar-day-width)] items-center justify-center"
              style={{ background: cellBg }}
            >
              <button
                type="button"
                disabled={!cell.inCurrentMonth || disabled}
                onMouseEnter={() => setHover(cell.date)}
                onClick={() => cell.inCurrentMonth && click(cell.date)}
                className={cn(
                  'relative z-[1] flex h-[var(--cmp-datepicker-calendar-day-circle-size)] w-[var(--cmp-datepicker-calendar-day-circle-size)] items-center justify-center rounded-[var(--cmp-datepicker-calendar-day-circle-radius)] border border-solid border-transparent bg-transparent text-[14px]',
                  !cell.inCurrentMonth
                    ? 'invisible pointer-events-none'
                    : disabled
                      ? 'cursor-not-allowed'
                      : 'cursor-pointer',
                  isEdge
                    ? 'bg-[var(--cmp-datepicker-calendar-day-select-bg)] text-[color:var(--cmp-datepicker-calendar-day-select-text)]'
                    : disabled
                      ? 'text-[color:var(--cmp-datepicker-calendar-day-disabled-text)]'
                      : 'text-[color:var(--cmp-datepicker-calendar-day-default-text)] hover:border-[color:var(--cmp-datepicker-calendar-day-hover-border)] hover:text-[color:var(--cmp-datepicker-calendar-day-hover-text)]',
                  isToday && !disabled && 'font-bold',
                )}
              >
                {cell.inCurrentMonth ? cell.day : ''}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderTimeSection = (which: 'start' | 'end', date: string | null, suffix: string) => {
    const active = !!date;
    const [yy = '', mm = '', dd = ''] = date ? date.split('-') : [];
    const hour = which === 'start' ? sH : eH;
    const minute = which === 'start' ? sM : eM;
    const num = (n: string, strip: boolean) =>
      active && n !== '' ? (strip ? String(Number(n)) : n) : '-';
    const INPUT_CLS =
      'h-[var(--cmp-datepicker-sm-height)] w-[40px] rounded-[var(--cmp-datepicker-sm-radius)] border border-solid border-[color:var(--cmp-datepicker-border-default)] bg-[var(--cmp-datepicker-bg-default)] text-center text-[12px] font-medium leading-[20px] text-[color:var(--cmp-datepicker-text-default)] outline-none placeholder:text-[color:var(--cmp-datepicker-text-disabled)] focus:border-[color:var(--cmp-datepicker-border-focus)] disabled:cursor-not-allowed';
    const LABEL = 'text-[color:var(--cmp-datepicker-calendar-time-date-label)]';
    const UNIT = 'text-[color:var(--cmp-datepicker-calendar-time-date-unit)]';
    return (
      <div
        className={cn(
          'flex flex-1 items-center justify-center gap-[var(--cmp-datepicker-calendar-time-section-gap)] rounded-[var(--cmp-datepicker-calendar-time-radius)] px-[var(--cmp-datepicker-calendar-time-paddingX)] py-[var(--cmp-datepicker-calendar-time-paddingY)] text-[12px] font-medium leading-[20px]',
          active
            ? 'bg-[var(--cmp-datepicker-calendar-time-bg-selected)]'
            : 'bg-[var(--cmp-datepicker-calendar-time-bg-default)]',
        )}
      >
        <div className="inline-flex items-center gap-[var(--cmp-datepicker-calendar-time-date-gap)]">
          <span className={LABEL}>{num(yy, false)}</span>
          <span className={UNIT}>년</span>
          <span className={LABEL}>{num(mm, true)}</span>
          <span className={UNIT}>월</span>
          <span className={LABEL}>{num(dd, true)}</span>
          <span className={UNIT}>일</span>
        </div>
        <div className="inline-flex items-center gap-[var(--cmp-datepicker-calendar-time-unit-gap)]">
          <input
            type="text"
            inputMode="numeric"
            maxLength={2}
            aria-label={`${suffix} 시`}
            disabled={!active}
            value={active ? hour : ''}
            placeholder="--"
            onChange={e => changeTime(which, 'h', e.target.value)}
            onBlur={() => blurTime(which, 'h')}
            className={INPUT_CLS}
          />
          <span className={LABEL}>:</span>
          <input
            type="text"
            inputMode="numeric"
            maxLength={2}
            aria-label={`${suffix} 분`}
            disabled={!active}
            value={active ? minute : ''}
            placeholder="--"
            onChange={e => changeTime(which, 'm', e.target.value)}
            onBlur={() => blurTime(which, 'm')}
            className={INPUT_CLS}
          />
        </div>
        <span className={UNIT}>{suffix}</span>
      </div>
    );
  };

  return (
    <div
      className={cn(
        'inline-flex flex-col gap-[var(--cmp-datepicker-calendar-gap)] rounded-[var(--cmp-datepicker-calendar-radius)] bg-[var(--cmp-datepicker-calendar-bg)] p-[var(--cmp-datepicker-calendar-paddingXY)] shadow-[2px_2px_12px_2px_rgba(0,0,0,0.2)] select-none',
        useTimePicker ? 'h-auto' : 'h-[364px]',
      )}
    >
      {/* 상단 바: 연도 네비(중앙) + 오늘 버튼(우측) */}
      <div className="relative flex items-center justify-center">
        <div className="flex items-center justify-center gap-[var(--cmp-datepicker-calendar-gap)] text-[14px] font-medium leading-[24px]">
          <SGhostButton icon="chevronLeft" size="xxs" ariaLabel="prevYear" onClick={goPrevYear} />
          <span className="inline-flex min-w-[40px] items-center justify-center text-center">
            {y}
          </span>
          <SGhostButton icon="chevronRight" size="xxs" ariaLabel="nextYear" onClick={goNextYear} />
        </div>
        <button
          type="button"
          onClick={goToday}
          className="absolute right-0 h-[24px] min-w-[37px] cursor-pointer bg-transparent text-center text-[12px] font-medium leading-[24px] text-[color:var(--cmp-datepicker-calendar-day-default-text)] hover:underline"
        >
          오늘
        </button>
      </div>
      {/* 듀얼 패널 */}
      <div className="flex gap-[var(--cmp-datepicker-calendar-range-panelGap)]">
        {renderPanel(y, m, true)}
        <SDivider vertical />
        {renderPanel(nextMonthYM.year, nextMonthYM.month, false)}
      </div>
      {/* 시간(시:분) 선택 푸터 — 시작(부터)/종료(까지) 섹션 */}
      {useTimePicker && (
        <>
          <SDivider />
          <div className="flex gap-[var(--cmp-datepicker-calendar-time-gap)]">
            {renderTimeSection('start', start, '부터')}
            {renderTimeSection('end', end, '까지')}
          </div>
        </>
      )}
    </div>
  );
}

/** SDateRangePicker — sd-date-range-picker 포팅. SField + SPopover(범위 캘린더). */
export function SDateRangePicker({
  value,
  onValueChange,
  onViewChange,
  size = 'sm',
  placeholder = 'YYYY-MM-DD ~ YYYY-MM-DD',
  selectable,
  maxRange,
  useTimePicker = false,
  disabled = false,
  width,
  name,
  rules,
  status,
  label,
  labelWidth,
  icon,
  iconColor,
  labelTooltip,
  labelTooltipProps,
  addonLabel,
  addonAlign,
  hint,
  error,
  errorMessage,
  className,
  style,
}: SDateRangePickerProps) {
  const portalContainer = usePortalContainer();
  const [open, setOpen] = useState(false);
  const [ruleError, setRuleError] = useState('');
  const [pendingStart, setPendingStart] = useState<string | null>(null);
  const hasValue = value != null && value[0] !== '' && value[1] !== '';
  // 시작일만 고른 상태면 `start ~` 프리뷰가 확정 값보다 우선
  const hasDisplay = pendingStart != null || hasValue;
  // `~`를 필드 중앙에 고정하기 위해 좌(시작)/우(종료)로 분리
  const startText = pendingStart ?? (hasValue ? value![0] : '');
  const endText = pendingStart ? '' : hasValue ? value![1] : '';

  // SForm 등록 — submit 시 폼이 이 필드를 일괄 검증한다 (폼 밖이면 no-op)
  useFormField({
    name,
    validate: () => {
      const msg = runRules(value, rules);
      setRuleError(msg);
      return msg === '';
    },
    resetValidation: () => setRuleError(''),
  });

  const resolvedError = error || ruleError !== '';
  const resolvedStatus =
    status ?? (rules && rules.length > 0 ? (ruleError !== '' ? 'error' : undefined) : undefined);
  // 트리거 치수 (sd-date-picker-trigger 재사용 — DATEPICKER_SIZE_MAP)
  const dims =
    size === 'md'
      ? { padX: 16, gap: 12, icon: 20, minW: 104, fontSize: 14, lineHeight: 24, radius: 6 }
      : { padX: 12, gap: 8, icon: 16, minW: 80, fontSize: 12, lineHeight: 20, radius: 4 };

  const select = (r: [string, string]) => {
    setPendingStart(null);
    onValueChange?.(r);
    if (rules && rules.length > 0) setRuleError(runRules(r, rules));
    // 시간 선택 모드에서는 사용자가 시:분을 조정할 수 있도록 드롭다운을 닫지 않는다
    if (!useTimePicker) setOpen(false);
  };
  // placeholder 미지정 + 시간 선택 모드이면 시:분 포함 기본 placeholder 사용
  const resolvedPlaceholder =
    useTimePicker && placeholder === 'YYYY-MM-DD ~ YYYY-MM-DD'
      ? 'YYYY-MM-DD HH:mm ~ YYYY-MM-DD HH:mm'
      : placeholder;

  return (
    <SField
      label={label}
      labelWidth={labelWidth}
      icon={icon}
      iconColor={iconColor}
      labelTooltip={labelTooltip}
      labelTooltipProps={labelTooltipProps}
      addonLabel={addonLabel}
      addonAlign={addonAlign}
      size={size}
      hint={hint}
      error={resolvedError}
      status={resolvedStatus}
      focused={open}
      errorMessage={ruleError !== '' ? ruleError : errorMessage}
      width={width}
      disabled={disabled}
      className={className}
      style={style}
    >
      <RPopover.Root
        open={open}
        onOpenChange={o => {
          if (disabled) return;
          setOpen(o);
          // 종료일 미선택 상태로 닫으면 프리뷰 취소(확정 값으로 복귀)
          if (!o) setPendingStart(null);
        }}
      >
        <RPopover.Trigger asChild>
          <button
            type="button"
            name={name}
            disabled={disabled}
            className="flex h-full w-full items-center overflow-hidden bg-transparent outline-none disabled:cursor-not-allowed"
            style={{ padding: `0 ${dims.padX}px`, gap: dims.gap, borderRadius: dims.radius }}
          >
            {/* 원본: 아이콘(왼쪽, 고정) + 텍스트(flex:1, 중앙정렬, ellipsis) */}
            <SIcon
              name="date"
              size={dims.icon}
              color={
                disabled
                  ? 'var(--cmp-datepicker-icon-disabled)'
                  : 'var(--cmp-datepicker-icon-default)'
              }
              className="flex-shrink-0"
            />
            <span
              className={cn(
                'flex-1 items-center',
                hasDisplay ? 'flex' : 'block truncate text-center',
              )}
              style={{
                minWidth: dims.minW,
                fontSize: dims.fontSize,
                lineHeight: `${dims.lineHeight}px`,
                color: disabled
                  ? 'var(--cmp-datepicker-text-disabled)'
                  : hasDisplay
                    ? 'var(--cmp-datepicker-text-default)'
                    : 'var(--cmp-field-hint-color)',
              }}
            >
              {hasDisplay ? (
                <>
                  <span className="min-w-0 flex-1 truncate text-right">{startText}</span>
                  <span className="px-[4px]">~</span>
                  <span className="min-w-0 flex-1 truncate text-left">{endText}</span>
                </>
              ) : (
                resolvedPlaceholder
              )}
            </span>
          </button>
        </RPopover.Trigger>
        <RPopover.Portal container={portalContainer}>
          <RPopover.Content
            side="bottom"
            align="start"
            sideOffset={4}
            className={cn('z-50', FLOATING_SLIDE_ANIM)}
          >
            <RangeCalendar
              value={value ?? null}
              selectable={selectable}
              maxRange={maxRange}
              useTimePicker={useTimePicker}
              onSelect={select}
              onPendingStartChange={setPendingStart}
              onViewChange={onViewChange}
            />
          </RPopover.Content>
        </RPopover.Portal>
      </RPopover.Root>
    </SField>
  );
}
